import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Box, Typography, IconButton, CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import { GapChip } from "../badges";
import ArticleGapPanel from "./ArticleGapPanel";

// Drill-down from a field → the articles affected by that field's gap → the article ficha to
// resolve in context. Data: GET /projects/:id/gaps?fieldKey=X (entity groups filtered to the
// field); we flatten to distinct articles.
//
// Single-drawer navigation (spec §7 — no drawer sobre drawer): selecting an article swaps the
// list for the article ficha rendered INLINE inside this same drawer, with a "back" control.
const useStyles = makeStyles((theme) => ({
  drawer: { width: "58vw", minWidth: 620, maxWidth: 1120 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    fontSize: 14,
  },
  name: { fontWeight: 600, color: "#4f46e5" },
  ref: { fontSize: 11, color: theme.palette.text.secondary },
}));

const FieldDrillPanel = ({ api, projectId, field, contracted, onClose }) => {
  const classes = useStyles();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // { id, name }

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api("GET", `/projects/${projectId}/gaps?fieldKey=${encodeURIComponent(field.fieldKey)}`);
    setLoading(false);
    // Flatten entity groups → distinct articles (a field may hit several entities per article).
    const byArticle = new Map();
    (r.ok ? r.data.groups ?? [] : []).forEach((g) =>
      (g.gaps || []).forEach((gap) => {
        if (!gap.articleId) return;
        if (!byArticle.has(gap.articleId))
          byArticle.set(gap.articleId, { id: gap.articleId, name: gap.articleName, ref: gap.articleReference, status: gap.status });
      })
    );
    setArticles([...byArticle.values()]);
  }, [api, projectId, field.fieldKey]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Drawer anchor="right" open onClose={onClose} classes={{ paper: classes.drawer }}>
      {selected ? (
        // Ficha shown inline in this same drawer (no second Drawer).
        <ArticleGapPanel
          api={api}
          articleId={selected.id}
          articleName={selected.name}
          contracted={contracted}
          inline
          backLabel="← Volver a la lista"
          onClose={() => {
            setSelected(null);
            load(); // refresh the affected-articles list after any change in the ficha
          }}
          onChanged={load}
        />
      ) : (
        <>
          <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" style={{ lineHeight: 1.2 }}>
                {field.displayName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {loading
                  ? "Cargando…"
                  : `${articles.length} artículo${articles.length !== 1 ? "s" : ""} afectado${articles.length !== 1 ? "s" : ""}`}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {loading ? (
            <Box textAlign="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : articles.length === 0 ? (
            <Box textAlign="center" py={3} style={{ color: "#94a3b8" }}>
              Sin artículos afectados.
            </Box>
          ) : (
            articles.map((a) => (
              <div key={a.id} className={classes.row} onClick={() => setSelected({ id: a.id, name: a.name })}>
                <GapChip status={a.status} />
                <span className={classes.name}>
                  {a.name || a.id.slice(0, 8)}
                  {a.ref && <span className={classes.ref}> · {a.ref}</span>}
                </span>
              </div>
            ))
          )}
        </>
      )}
    </Drawer>
  );
};

export default FieldDrillPanel;
